import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { SortDir } from '../api/searchTypes';
import { sortLabel } from '../state/searchParams';
import { gridTemplate, resultColumns } from './resultColumns';
import { SortableHeaderCell } from './SortableHeaderCell';

export interface ResultsHeaderProps {
    sort: string;
    dir: SortDir;
    sortableKeys: string[];
    sortInfo: Record<string, string>;
    marketSort: string;
    marketDir: SortDir;
    marketSortOptions: string[];
    marketSortInfo: Record<string, string>;
    pageAllSelected: boolean;
    pageSomeSelected: boolean;
    onSort: (key: string) => void;
    onMarketSortChange: (value: string) => void;
    onMarketDirChange: (dir: SortDir) => void;
    onTogglePageSelection: (value: boolean) => void;
}

const SELECT_SX = {
    fontSize: '10px',
    fontFamily: 'inherit',
    color: 'text.secondary',
    bgcolor: 'transparent',
    border: 'none',
    outline: 'none',
    p: 0,
    m: 0,
    height: 14,
    lineHeight: '14px',
    cursor: 'pointer',
} as const;

export function ResultsHeader({
    sort,
    dir,
    sortableKeys,
    sortInfo,
    marketSort,
    marketDir,
    marketSortOptions,
    marketSortInfo,
    pageAllSelected,
    pageSomeSelected,
    onSort,
    onMarketSortChange,
    onMarketDirChange,
    onTogglePageSelection,
}: ResultsHeaderProps) {
    const columns = resultColumns();

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.default',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'grid',
                gridTemplateColumns: gridTemplate(),
                alignItems: 'center',
                columnGap: 1,
                px: 1.25,
                height: 26,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minWidth: 0,
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                    }}
                >
                    EVENT
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                    markets by
                </Typography>
                <Box
                    component="select"
                    value={marketSort}
                    onChange={e =>
                        onMarketSortChange(
                            (e.target as HTMLSelectElement).value
                        )
                    }
                    title={marketSortInfo[marketSort] ?? ''}
                    sx={SELECT_SX}
                >
                    {marketSortOptions.map(option => (
                        <option key={option} value={option}>
                            {sortLabel(option)}
                        </option>
                    ))}
                </Box>
                <Box
                    onClick={() =>
                        onMarketDirChange(marketDir === 'desc' ? 'asc' : 'desc')
                    }
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: 'text.disabled',
                    }}
                >
                    {marketDir === 'desc' ? (
                        <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    ) : (
                        <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                    )}
                </Box>
            </Box>

            {columns.map(column => {
                const sortable = Boolean(
                    column.sort && sortableKeys.includes(column.sort)
                );
                const active = sortable && column.sort === sort;
                return (
                    <SortableHeaderCell
                        key={column.key}
                        label={column.label}
                        align={column.align}
                        sortKey={column.sort}
                        sortable={sortable}
                        active={active}
                        dir={dir}
                        info={sortInfo[column.sort as string] ?? ''}
                        unsortableTitle="Not sortable in this mode"
                        onSort={onSort}
                    />
                );
            })}

            <Box />

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip
                    title="Select every market on this page"
                    placement="top"
                    disableInteractive
                >
                    <Checkbox
                        size="small"
                        checked={pageAllSelected}
                        indeterminate={pageSomeSelected}
                        onChange={() => onTogglePageSelection(!pageAllSelected)}
                        sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 15 } }}
                    />
                </Tooltip>
            </Box>
        </Box>
    );
}
